import type { ReactNode } from 'react'
import { Button, Card, CardBody } from '../../ui'

/** The From address of every sign-in email (IEmailProvider: the `outbound` stream's sender). */
export const SIGN_IN_SENDER = 'no-reply@auth.defenssive.dev'

export interface MagicLinkSentProps {
  email: string
  /**
   * Whether to render the fake inbox.
   *
   * True only in the prototype. In the real flow there is a real email, and showing a button
   * that signs you in without one would defeat the entire point of a link that proves control
   * of an address.
   */
  simulated?: boolean
  /**
   * Shown here and nowhere else — never emailed, never in the URL, never logged.
   *
   * The link travels through the customer's mail pipeline and is therefore readable by anyone
   * with access to Safe Links or URL-click telemetry. This code does not, which is what makes
   * holding the link insufficient. It is only needed when finishing on a different device.
   */
  confirmationCode?: string | null
  onOpenLink?: () => void
  onResend: () => void
  onDifferentMethod: () => void
  /** A resend is in flight. */
  busy?: boolean
  /** Why the last resend did not go through. Shown here: this screen, not the form, is on display. */
  notice?: string | null
  /** A resend went through. The screen must say so -- and that the earlier link and code are gone. */
  resent?: boolean
  /**
   * The bot check, drawn above the buttons when it is on. A check passes once, so a resend needs a
   * fresh one, and the one passed on the form is spent by the request that brought the person here.
   */
  challenge?: ReactNode
  /** The check is on and has not been passed yet: a resend cannot be requested. */
  challengePending?: boolean
  /** Why Resend is disabled, in words, when the cause is the check. See SignIn. */
  challengeHint?: string | null
}

/**
 * "Check your inbox", with a simulated inbox so the prototype stays clickable.
 *
 * The simulated card is prototype scaffolding and is replaced in step 5 by the real emailed
 * link. Two things about that link are settled and must survive the replacement: it is consumed
 * by POST rather than GET, because Defender for Office 365 Safe Links fetches every URL in a
 * message and would otherwise burn the single use before the customer clicked it; and it stays
 * single-use with a 15-minute life.
 */
export function MagicLinkSent({
  email,
  simulated = false,
  confirmationCode,
  onOpenLink,
  onResend,
  onDifferentMethod,
  busy = false,
  notice = null,
  resent = false,
  challenge,
  challengePending = false,
  challengeHint = null,
}: MagicLinkSentProps) {
  return (
    <div className="ob-screen ob-screen-narrow rise-in" style={{ paddingTop: 'calc(var(--space-8) * 1.5)' }}>
      <div className="label-mono" style={{ marginBottom: 'var(--space-3)' }}>
        Check your inbox
      </div>
      <h2>Sign-in link requested for {email}</h2>
      <p className="text-muted" style={{ fontSize: 14 }}>
        If this address can sign in, a link is on its way. Open it on any device to finish signing in.
        The link works once and expires in 15
        minutes. Nothing is created in your Microsoft tenant at this stage.
      </p>

      <hr className="hr" />

      {confirmationCode ? (
        <div className="confirmation-code">
          <div className="label-mono">Finishing on another device?</div>
          <div className="confirmation-code-value tnum" aria-label={`Confirmation code ${confirmationCode.split('').join(' ')}`}>
            {confirmationCode}
          </div>
          <p className="form-footnote" style={{ margin: 0 }}>
            Enter this after opening the link. It is only shown here — we never send it by email,
            so a copy of the link on its own is not enough to sign in as you.
          </p>
        </div>
      ) : null}

      {simulated ? (
        <Card kicker="Simulated inbox" title="Your Defenssive sign-in link">
          <CardBody>
            Sign in to Defenssive AI Security Manager as {email}. If you did not request this,
            ignore the message — no account is created without it.
          </CardBody>
          <div>
            <Button variant="primary" onClick={onOpenLink}>
              Open sign-in link
            </Button>
          </div>
        </Card>
      ) : (
        /*
          WHAT TO DO WHEN NOTHING ARRIVES, said plainly (owner, 2026-10-08/09). The sign-in mail to a
          Microsoft 365 mailbox was quarantined as Phish on every send, with SPF, DKIM and DMARC
          passing (docs/email-sending-domain.md), and only an administrator can release it, so "check
          your junk folder" sent people to the one place it was not. That turned out to be the owner's
          tenant protecting its own defenssive.com (domain impersonation); other tenants receive it, so
          the line says "may be", not "probably". And a fourth request inside 15
          minutes sends nothing while this screen looks the same (SignInRateLimiter, deliberately
          silent), so "Resend" pressed again and again only made it worse. Every line is the same for
          every address: it says nothing about whether this one exists, was limited or was sent.
        */
        <section className="inbox-help" aria-labelledby="inbox-help-heading">
          <h3 id="inbox-help-heading">Not there after a few minutes?</h3>
          <ul>
            <li>
              New self-service accounts require a work email. Known personal or temporary email
              domains receive a link only for an existing account or an owner-approved invitation.
            </li>
            <li>Check your junk folder.</li>
            <li>
              <strong>On Microsoft 365, it may be held in quarantine.</strong> Security policies can
              hold mail from a sender your organization has not seen before, even when it passes every
              authentication check, and only an administrator can release it. Ask your Microsoft
              365 administrator to release it and to allow{' '}
              <span className="mono">{SIGN_IN_SENDER}</span>.
            </li>
            <li>
              Requests are limited to three every 15 minutes for one address. Wait before asking
              again: each new link replaces the last.
            </li>
          </ul>
        </section>
      )}

      {/* Resend used to change nothing on screen. A failure was set on the form, which is not
          showing, so the person waited for mail that was never requested; a success silently
          swapped the code and killed the first email's link -- often the one that turns up late
          out of quarantine. The same words for every address, so this is no enumeration oracle. */}
      {notice ? (
        <div className="sign-in-notice" role="alert">
          {notice}
        </div>
      ) : resent ? (
        // "Requested", not "sent": the answer is identical whether or not a link went out (an
        // unknown or rate-limited address gets the same 202), so this cannot claim more.
        <p className="form-footnote" role="status">
          Requested again. Use the newest email when it arrives — a new link replaces the earlier
          one{confirmationCode ? ', and the code above replaces the previous code' : ''}.
        </p>
      ) : null}

      {challenge}
      {challengeHint ? (
        <p id="challenge-hint" className="form-footnote" role="status">
          {challengeHint}
        </p>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        <Button
          variant="secondary"
          onClick={onResend}
          disabled={busy || challengePending}
          aria-describedby={challengeHint ? 'challenge-hint' : undefined}
        >
          {busy ? 'Sending…' : 'Resend link'}
        </Button>
        <Button variant="ghost" onClick={onDifferentMethod}>
          Use a different method
        </Button>
      </div>
    </div>
  )
}
